/**
 * User Notification & Push Module API Client
 * Compatible with /admin/user-notification backend endpoints:
 *  - POST   /admin/user-notification/send-push
 *  - GET    /admin/user-notification/list
 *  - DELETE /admin/user-notification/delete/:id
 */

const getBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_BASE_URL || "https://api.minezeta.com/admin";
  // If envUrl already ends with /admin, strip trailing slashes
  return envUrl.replace(/\/+$/, "");
};

const getUserNotificationEndpoint = (path = "") => {
  const base = getBaseUrl();
  const cleanPath = path.replace(/^\/+/, "");
  // Check if base already includes /admin
  if (base.endsWith("/admin")) {
    return `${base}/user-notification${cleanPath ? `/${cleanPath}` : ""}`;
  }
  return `${base}/admin/user-notification${cleanPath ? `/${cleanPath}` : ""}`;
};

const getAuthHeaders = (isFormData = false) => {
  const token = localStorage.getItem("token");
  const headers = {};
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

const handleApiResponse = async (response) => {
  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");
  const result = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const errorMsg =
      (typeof result === "object" && (result?.message || result?.error)) ||
      (typeof result === "string" ? result : "Request failed");
    throw new Error(errorMsg);
  }

  return result;
};

/**
 * Supported App Deep-Link Routes matching Section 3 of USER_NOTIFICATION_README.md
 */
export const APP_ROUTES = [
  { label: "Home (Default)", value: "/home", icon: "🏠", description: "Home Dashboard Screen" },
  { label: "Wallet", value: "/wallet", icon: "💳", description: "Wallet / Balances Screen" },
  { label: "Leaderboard", value: "/leaderboard", icon: "🏆", description: "Event / Mining Leaderboard" },
  { label: "Refer & Earn", value: "/refer", icon: "👥", description: "Refer & Earn Screen" },
  { label: "Zeta Journey", value: "/zeta-journey", icon: "🗺️", description: "Zeta Journey Milestone Screen" },
  { label: "Community", value: "/community", icon: "💬", description: "Social & Community Channels" },
  { label: "Account / Profile", value: "/account", icon: "👤", description: "Profile & Settings Screen" },
  { label: "KYC Verification", value: "/kyc", icon: "🆔", description: "KYC Verification Screen" },
  { label: "Announcement", value: "/announcement", icon: "📢", description: "Announcements Screen" },
  { label: "Games Hub", value: "/game", icon: "🎮", description: "Games Hub Screen" },
  { label: "Tap Rush Game", value: "/tap-rush", icon: "👆", description: "Tap Game Screen" },
  { label: "777 Lucky Slot Game", value: "/lucky-slot", icon: "🎰", description: "777 Slot Machine Game Screen" },
  { label: "Lucky Spinner Game", value: "/spinner", icon: "🎡", description: "Wheel of Fortune / Spinner Screen" },
  { label: "Boom Cards Game", value: "/boom-cards", icon: "🃏", description: "Boom Cards Game Screen" },
];

/**
 * Send Push Notification (Bulk or Particular User)
 * @param {FormData|Object} data
 * @param {string} [data.mode='bulk'] - 'bulk' | 'particular'
 * @param {number|string} [data.userId] - required if mode === 'particular'
 * @param {string} data.title
 * @param {string} data.message
 * @param {string} [data.redirectRoute='/home']
 * @param {string} [data.url]
 * @param {File} [data.image]
 * @param {string} [data.imageUrl]
 */
export const sendUserPushNotification = async (data) => {
  const isFormData = data instanceof FormData;
  const url = getUserNotificationEndpoint("send-push");

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: getAuthHeaders(isFormData),
      body: isFormData ? data : JSON.stringify(data),
    });
    return await handleApiResponse(response);
  } catch (err) {
    // If send-push fails with 404, fallback to /send alias
    if (err.message && err.message.includes("404")) {
      const aliasUrl = getUserNotificationEndpoint("send");
      const fallbackResponse = await fetch(aliasUrl, {
        method: "POST",
        headers: getAuthHeaders(isFormData),
        body: isFormData ? data : JSON.stringify(data),
      });
      return await handleApiResponse(fallbackResponse);
    }
    throw err;
  }
};

/**
 * List Sent Notifications History (Admin)
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.limit=10]
 * @param {string} [params.search]
 * @param {string} [params.mode='all'] - 'all' | 'bulk' | 'particular'
 * @param {number|string} [params.userId]
 */
export const getUserNotificationList = async ({
  page = 1,
  limit = 10,
  search = "",
  mode = "all",
  userId = "",
} = {}) => {
  const queryParams = new URLSearchParams();
  if (page) queryParams.append("page", page);
  if (limit) queryParams.append("limit", limit);
  if (search && search.trim()) queryParams.append("search", search.trim());
  if (mode && mode !== "all") queryParams.append("mode", mode);
  if (userId) queryParams.append("userId", userId);

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const url = `${getUserNotificationEndpoint("list")}${queryString}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(false),
  });

  return handleApiResponse(response);
};

/**
 * Delete Sent Notification Record (Admin)
 * @param {number|string} id
 */
export const deleteUserNotification = async (id) => {
  const url = getUserNotificationEndpoint(`delete/${encodeURIComponent(id)}`);

  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: getAuthHeaders(false),
    });
    return await handleApiResponse(response);
  } catch (err) {
    // If delete/:id 404s, fallback to /:id
    if (err.message && err.message.includes("404")) {
      const aliasUrl = getUserNotificationEndpoint(encodeURIComponent(id));
      const fallbackResponse = await fetch(aliasUrl, {
        method: "DELETE",
        headers: getAuthHeaders(false),
      });
      return await handleApiResponse(fallbackResponse);
    }
    throw err;
  }
};
